import { IconContext } from '@phosphor-icons/react'
import classNames from 'classnames'
import z from 'zod'
import { BUTTON_ICON_CONTEXT } from './button-icon-context.ts'
import * as styles from './split-outline-button.css.ts'

type SplitOutlineButtonProps = {
  children: React.ReactNode
  className?: string
  'aria-label'?: string
}

/**
 * Two related actions drawn as one small outline button, split by a divider — for example the Sort
 * button's sort type and its direction. Each half is its own `SplitOutlineButtonSegment`, so either can
 * be a menu trigger (wrap it in a Radix `Trigger asChild`) or a plain button.
 */
export const SplitOutlineButton: React.FC<SplitOutlineButtonProps> = ({
  children,
  className,
  'aria-label': ariaLabel,
}) => (
  <IconContext.Provider value={BUTTON_ICON_CONTEXT}>
    <div role="group" aria-label={ariaLabel} className={classNames(styles.group, className)}>
      {children}
    </div>
  </IconContext.Provider>
)

export const SplitOutlineButtonSegmentPositionSchema = z.enum(['start', 'end'])
type SplitOutlineButtonSegmentPosition = z.infer<typeof SplitOutlineButtonSegmentPositionSchema>

type SplitOutlineButtonSegmentProps = React.ComponentProps<'button'> & {
  position: SplitOutlineButtonSegmentPosition
}

export const SplitOutlineButtonSegment: React.FC<SplitOutlineButtonSegmentProps> = ({
  position,
  className,
  type = 'button',
  ...props
}) => <button type={type} className={classNames(styles.segment({ position }), className)} {...props} />
